/**
 * Hint quality — the per-student view.
 *
 * One row per student. The headline is the reliance ratio — questions where a
 * hint was used over questions attempted — because a raw hint count means
 * nothing without the work it sits against: 12 hints over 200 questions is a
 * different student from 12 over 15.
 *
 * A teacher sees their whole roster, including students who have never taken a
 * hint. A zero row is a real answer to "who leans on hints", and a list built
 * from hint data alone would quietly omit everyone who doesn't.
 *
 * See docs/FeatureDesigns/HintSystem/hint-quality-review.md §5.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api';
import {
  DataTable, SearchFilterBar, Pagination,
  type ColumnDef, type FilterDef, type SortState,
} from '../components';
import { createHintQualityApi, sinceDateFromRange } from './hintQualityApi';
import type { HintQualityStudentRow, HintQualityAssignmentOption } from './types';

const PAGE_SIZE = 25;

const DATE_RANGES = [
  { value: '30', label: 'Last 30 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '', label: 'All time' },
];

interface Filters {
  dateRange: string;
  search: string;
  questionSetId: string;
}

const DEFAULT_FILTERS: Filters = { dateRange: '30', search: '', questionSetId: '' };

function formatAssignmentLabel(a: { title: string; date?: string }): string {
  if (!a.date) return a.title;
  const d = new Date(a.date);
  if (Number.isNaN(d.getTime())) return a.title;
  return `${a.title} (${d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })})`;
}

function formatWhen(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' }) +
    ', ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

export interface HintQualityStudentsTabProps {
  /** Opens the sessions view filtered to one student. */
  onStudentClick?: (row: HintQualityStudentRow) => void;
}

export function HintQualityStudentsTab({ onStudentClick }: HintQualityStudentsTabProps) {
  const api = useApi();
  const hintQualityApi = useMemo(() => createHintQualityApi(api), [api]);
  const [assignmentOptions, setAssignmentOptions] = useState<HintQualityAssignmentOption[]>([]);

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ sortBy: 'hintsShown', sortOrder: 'desc' });

  const filterDefs = useMemo<FilterDef[]>(() => [
    { key: 'dateRange', kind: 'select', placeholder: 'Date range', options: DATE_RANGES, required: true },
    {
      key: 'questionSetId',
      kind: 'select',
      placeholder: 'All assignments',
      options: assignmentOptions.map(a => ({ value: a.id, label: formatAssignmentLabel(a) })),
    },
  ], [assignmentOptions]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['hint-quality-students', page, filters, sort],
    queryFn: () => hintQualityApi.getStudents({
      page,
      pageSize: PAGE_SIZE,
      sinceDate: sinceDateFromRange(filters.dateRange),
      search: filters.search || undefined,
      questionSetId: filters.questionSetId || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortOrder,
    }),
  });

  const rows = data?.items || [];
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  useEffect(() => {
    if (data?.assignments?.length) setAssignmentOptions(data.assignments);
  }, [data?.assignments]);

  // Reset to page 1 whenever the result set shrinks past the current page —
  // otherwise filtering from page 3 lands on an empty page.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) setPage(1);
  }, [totalPages, page]);

  const columns = useMemo<ColumnDef<HintQualityStudentRow>[]>(() => [
    {
      key: 'name',
      header: 'Student',
      sortable: true,
      render: r => (
        <div className="hintq-stack">
          <strong>{r.name}</strong>
          {r.email ? <span className="hintq-sub">{r.email}</span> : null}
        </div>
      ),
    },
    {
      key: 'hintsShown',
      header: 'Hints',
      sortable: true,
      className: 'hintq-num',
      render: r => (r.hintsShown === 0
        ? <span className="hintq-muted">0</span>
        : (
          <div className="hintq-stack">
            <strong>{r.hintsShown}</strong>
            {r.hintsPrewritten > 0 && (
              <span className="hintq-sub">{r.hintsLlm} AI · {r.hintsPrewritten} ready</span>
            )}
          </div>
        )),
    },
    {
      key: 'questionsWithHints',
      header: 'Questions w/ hints',
      sortable: true,
      className: 'hintq-num',
      render: r => (
        <div className="hintq-stack">
          <strong>
            {r.questionsWithHints}
            <span className="hintq-ratio-sep"> / </span>
            <span className="hintq-ratio-total">{r.questionsAttempted}</span>
          </strong>
          <span className="hintq-sub" title="Questions with a hint, over questions attempted">
            {r.questionsAttempted > 0
              ? `${Math.round((r.questionsWithHints / r.questionsAttempted) * 100)}% of attempts`
              : 'no attempts'}
          </span>
        </div>
      ),
    },
    {
      key: 'sessions',
      header: 'Attempts w/ hints',
      sortable: true,
      className: 'hintq-num',
      render: r => r.sessions || <span className="hintq-muted">0</span>,
    },
    {
      key: 'thumbsUp',
      header: '👍',
      sortable: true,
      className: 'hintq-num',
      render: r => (r.thumbsUp
        ? <span className="hintq-rating hintq-rating--up">{r.thumbsUp}</span>
        : <span className="hintq-muted">—</span>),
    },
    {
      key: 'thumbsDown',
      header: '👎',
      sortable: true,
      className: 'hintq-num',
      render: r => (r.thumbsDown
        ? <span className="hintq-rating hintq-rating--down">{r.thumbsDown}</span>
        : <span className="hintq-muted">—</span>),
    },
    {
      key: 'lastHintAt',
      header: 'Last hint',
      sortable: true,
      render: r => <span className="hintq-sub">{formatWhen(r.lastHintAt)}</span>,
    },
  ], []);

  if (error) {
    return <div className="hintq-error">Couldn't load students: {(error as Error).message}</div>;
  }

  return (
    <>
      <SearchFilterBar
        filters={filterDefs}
        values={filters as unknown as Record<string, string>}
        onChange={v => { setFilters(f => ({ ...f, ...(v as Partial<Filters>) })); setPage(1); }}
        searchPlaceholder="Search student name or email…"
        loading={isFetching}
        hideFetch
        trailingActions={totalPages > 1 ? (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={data?.total}
            onPageChange={setPage}
            disabled={isFetching}
          />
        ) : undefined}
      />

      <DataTable
        columns={columns}
        data={rows}
        rowKey={r => r.id}
        onRowClick={onStudentClick}
        rowClickable={r => !!onStudentClick && r.sessions > 0}
        sort={sort}
        onSortChange={s => { setSort(s); setPage(1); }}
        loading={isLoading}
        emptyMessage={filters.search || filters.questionSetId
          ? 'No students match these filters.'
          : 'No students yet.'}
      />
    </>
  );
}
